/**
 * O que faz: Lista TODOS os anúncios do período numa área com rolagem dentro do card, permite editar
 *            o apelido direto na linha (ícone de lápis) e, com o Meta Ads conectado, agrupa a lista
 *            por anúncio, conjunto ou campanha (abas iguais às da aba Vendas).
 * Onde usa: Grade de gráficos do dashboard.
 * Conexões: useRankingOrigensDashboard (lista completa), ResumoDashboard (prévia enquanto carrega),
 *           AdPerformanceModal (busca e ordenação), useAdAliases (apelido) e drill-down de leads.
 * Tabelas: ad_aliases via useAdAliases.saveAlias (upsert só ao clicar em salvar); o resto são
 *          agregados somente leitura do dashboard.
 * Data: 31/08/2026 (apelido no card em 05/10/2026; rolagem e abas em 09/10/2026).
 */

import { useState } from 'react';
import { ArrowUpRight, Check, Pencil, Search, Target, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GavetaSuave } from '@/components/ui/gaveta-suave';
import { cn } from '@/lib/utils';
import { useAdAliases } from '@/hooks/useAdAliases';
import { usePrefetchRankingOrigens, useRankingOrigensDashboard } from '@/hooks/dashboard/useRankingOrigensDashboard';
import { AdPerformanceModal } from './AdPerformanceModal';
import type { ContextoDashboard, DimensaoRankingAnuncios, ResumoDashboard } from '@/features/dashboard/data/dashboardContracts';

interface AdPerformanceData { anuncio: string; apelido?: string | null; leads: number; conversoes: number; }
// Linha de conjunto/campanha clicada: chave normalizada (filtro do modal), nome para o título e total da linha.
export interface OrigemMetaClicada { dimensao: 'conjunto' | 'campanha'; chave: string; nome: string; leads: number; }
interface AdPerformanceCardProps {
  data: AdPerformanceData[];
  onAdClick?: (adName: string) => void;
  onOrigemMetaClick?: (origem: OrigemMetaClicada) => void;
  adminMode?: boolean;
  targetClinicaId?: string;
  contexto?: ContextoDashboard;
  resumoAnuncios?: ResumoDashboard['anuncios'];
  isUpdating?: boolean;
}

const DIMENSOES: Array<{ valor: DimensaoRankingAnuncios; rotulo: string; plural: string; semOrigem: string }> = [
  { valor: 'anuncio', rotulo: 'Anúncio', plural: 'anúncios', semOrigem: '' },
  { valor: 'conjunto', rotulo: 'Conjunto', plural: 'conjuntos', semOrigem: 'sem conjunto identificado' },
  { valor: 'campanha', rotulo: 'Campanha', plural: 'campanhas', semOrigem: 'sem campanha identificada' },
];

export const AdPerformanceCard = ({ data, onAdClick, onOrigemMetaClick, adminMode = false, targetClinicaId, contexto, resumoAnuncios, isUpdating = false }: AdPerformanceCardProps) => {
  const [modalAberto, setModalAberto] = useState(false);
  const [dimensao, setDimensao] = useState<DimensaoRankingAnuncios>('anuncio');
  // Edição inline: guarda o nome ORIGINAL do anúncio em edição (é a chave gravada em ad_aliases).
  const [editando, setEditando] = useState<string | null>(null);
  const [apelido, setApelido] = useState('');
  const [erroApelido, setErroApelido] = useState<string | null>(null);
  const [apelidoSalvo, setApelidoSalvo] = useState(false);
  const { getAliasForAd, saveAlias, isSaving } = useAdAliases(adminMode ? targetClinicaId : undefined);

  // A lista por anúncio sempre carrega: além dos itens, ela diz se o Meta Ads está conectado
  // (é isso que libera as abas). Quando a aba ativa é "Anúncio", as duas chamadas usam a mesma
  // chave e o React Query faz uma leitura só.
  const rankingAnuncios = useRankingOrigensDashboard(contexto, 'anuncio');
  const metaConectado = rankingAnuncios.data?.metaConectado ?? false;
  // Se a conexão some (ex.: admin troca de clínica), volta para "Anúncio" em vez de mostrar aba vazia.
  const dimensaoAtiva: DimensaoRankingAnuncios = metaConectado ? dimensao : 'anuncio';
  const ranking = useRankingOrigensDashboard(contexto, dimensaoAtiva);
  const preCarregar = usePrefetchRankingOrigens(contexto);
  // Na troca de aba a lista anterior continua na tela, esmaecida, até a nova chegar (isPlaceholderData).
  // O que está desenhado segue a aba DOS DADOS, não a do botão: nunca mostramos anúncios rotulados como conjuntos.
  const trocando = Boolean(ranking.isPlaceholderData);
  const dimensaoExibida: DimensaoRankingAnuncios = ranking.data?.dimensao ?? dimensaoAtiva;
  const configuracao = DIMENSOES.find(item => item.valor === dimensaoExibida)!;
  const configuracaoAtiva = DIMENSOES.find(item => item.valor === dimensaoAtiva)!;
  const porAnuncio = dimensaoExibida === 'anuncio';
  const indiceAba = DIMENSOES.findIndex(item => item.valor === dimensaoAtiva);

  // Por anúncio, enquanto a lista completa não chega (ou se falhar), mostramos a prévia do resumo
  // (10 primeiros) para o card nunca ficar vazio. Os totais do resumo já são os globais do período.
  const previa = resumoAnuncios?.itens.map(item => ({ chave: item.chave, nome: item.nome, apelido: item.apelido, leads: item.leads, conversoes: item.conversoes }))
    ?? data.map(item => ({ chave: item.anuncio, nome: item.anuncio, apelido: item.apelido ?? null, leads: item.leads, conversoes: item.conversoes }));
  const itens = ranking.data?.itens ?? (porAnuncio ? previa : []);
  const totalItens = ranking.data?.totalItens ?? (porAnuncio ? (resumoAnuncios?.totalItens ?? data.length) : 0);
  const totalLeads = ranking.data?.totalLeads ?? (porAnuncio ? (resumoAnuncios?.totalLeads ?? data.reduce((soma, item) => soma + item.leads, 0)) : 0);
  const totalConversoes = ranking.data?.totalConversoes ?? (porAnuncio ? (resumoAnuncios?.totalConversoes ?? data.reduce((soma, item) => soma + item.conversoes, 0)) : 0);
  const semOrigem = ranking.data?.semOrigem ?? 0;
  // Só sem nenhum dado anterior para manter na tela (raro: o dado de "Anúncio" já veio no carregamento).
  const carregandoAgrupado = !porAnuncio && ranking.isLoading && !ranking.data;
  const erroLista = Boolean(ranking.error) && !ranking.data;
  const temItens = itens.length > 0;
  // Durante atualização do período ou gravação, o lápis fica bloqueado para não editar dado velho.
  const bloqueado = isUpdating || isSaving;

  const trocarDimensao = (nova: DimensaoRankingAnuncios) => {
    // Sair da aba "Anúncio" no meio de uma edição descartaria o texto sem aviso; cancelamos antes.
    setEditando(null);
    setErroApelido(null);
    setDimensao(nova);
  };

  const iniciarEdicao = (nomeOriginal: string, nomeExibido: string) => {
    if (bloqueado) return;
    setEditando(nomeOriginal);
    setApelido(nomeExibido);
    setErroApelido(null);
    setApelidoSalvo(false);
  };

  const cancelar = () => {
    setEditando(null);
    setApelido('');
    setErroApelido(null);
  };

  const salvar = async (nomeOriginal: string) => {
    // Validação: apelido vazio apagaria o nome visível do anúncio; pede um texto antes de gravar.
    if (!apelido.trim()) { setErroApelido('Digite um apelido antes de salvar.'); return; }
    try {
      // Escrita em ad_aliases só nesta ação explícita; o hook invalida apelidos e as consultas do dashboard.
      await saveAlias({ adNameOriginal: nomeOriginal, alias: apelido.trim() });
      cancelar();
      setApelidoSalvo(true);
    } catch {
      setApelidoSalvo(false);
      setErroApelido('Não foi possível salvar o apelido. Tente novamente.');
    }
  };

  const subtitulo = carregandoAgrupado || (trocando && dimensaoExibida !== dimensaoAtiva)
    ? `Carregando ${configuracaoAtiva.plural}…`
    : temItens
      ? `${totalItens} ${totalItens === 1 ? configuracao.rotulo.toLocaleLowerCase('pt-BR') : configuracao.plural} · ordenados por leads`
      : 'Ranking de anúncios no período';

  // Altura fixa: no desktop o card fica lado a lado com "Origem das Conversas" e a grade estica os dois
  // para a mesma altura (lg:h-auto + grid stretch). A lista usa base 0px (grow + basis-0), então NÃO empurra a altura:
  // só ocupa o espaço que sobra e rola. No celular (um card por linha) a altura é fixa, próxima à do gráfico.
  // Assim o card não cresce nem encolhe ao trocar de aba. No desktop a lista tem altura mínima de
  // ~5 linhas (lg:min-h-[16.5rem]): isso define a altura da linha da grade, e o gráfico ao lado acompanha.
  return <div className="flex h-[29.5rem] flex-col rounded-2xl border border-border bg-card p-4 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)] sm:h-[31.5rem] sm:p-6 lg:h-auto">
    <div className="mb-3 flex min-h-9 shrink-0 items-start justify-between gap-3">
      <div className="min-w-0"><h3 className="text-[15px] font-semibold text-foreground">Performance de anúncios</h3><p className="mt-0.5 text-[13px] text-muted-foreground">{subtitulo}</p></div>
      {/* Busca e ordenação continuam no modal; ele só pesquisa anúncios (é onde moram os apelidos). */}
      {porAnuncio && temItens && !trocando && <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={isUpdating}
        onClick={() => setModalAberto(true)}
        className="h-9 shrink-0 gap-1.5 rounded-full px-3 text-xs font-semibold text-accent-foreground hover:bg-accent hover:text-accent-foreground"
      >
        {isUpdating ? 'Atualizando…' : <><Search className="h-3.5 w-3.5" aria-hidden="true" />Buscar</>}
      </Button>}
    </div>

    {/* Abas só aparecem com o Meta Ads conectado; entram deslizando quando a resposta chega. */}
    <div className="shrink-0"><GavetaSuave aberta={metaConectado}>
      <div role="group" aria-label="Agrupar anúncios por" className="relative mb-3 grid grid-cols-3 rounded-full bg-secondary p-1">
        {/* Destaque branco único que DESLIZA até a aba escolhida (só transform; some com movimento reduzido
            porque a transição é desligada e ele apenas troca de lugar). Largura de 1/3 da área interna. */}
        <span aria-hidden="true"
          className="pointer-events-none absolute bottom-1 left-1 top-1 w-[calc((100%-0.5rem)/3)] rounded-full bg-card shadow-[0_1px_2px_rgba(19,41,27,0.08),0_3px_8px_rgba(19,41,27,0.06)] transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${indiceAba * 100}%)` }} />
        {DIMENSOES.map(item => <button key={item.valor} type="button" aria-pressed={dimensaoAtiva === item.valor}
          onClick={() => trocarDimensao(item.valor)}
          // Pré-carrega a aba quando o mouse chega nela (ou o foco do teclado): o clique já encontra a lista.
          onPointerEnter={() => preCarregar(item.valor)}
          onFocus={() => preCarregar(item.valor)}
          className={cn(
            'relative z-10 min-h-9 rounded-full px-2 text-[13px] font-semibold transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
            dimensaoAtiva === item.valor ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}>{item.rotulo}</button>)}
      </div>
    </GavetaSuave></div>

    {carregandoAgrupado ? <p role="status" className="flex min-h-0 flex-1 items-center justify-center rounded-xl bg-muted p-4 text-center text-sm text-muted-foreground">Carregando {configuracao.plural} dos leads…</p>
      : !porAnuncio && erroLista ? <div role="alert" className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-xl bg-muted p-4 text-center text-sm text-muted-foreground">
        <span>Não foi possível carregar os {configuracao.plural}. Tente novamente.</span>
        <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => void ranking.refetch()}>Tentar de novo</Button>
      </div>
      : temItens
        // Rolagem no próprio dono do scroll, com o fade padrão (crm-scroll-fade, AGENTS.md).
        // overscroll-auto: ao chegar no fim da lista, a roda do mouse continua rolando a página.
        // tabIndex: nas abas de conjunto/campanha as linhas não são clicáveis, então a lista precisa
        // receber foco para rolar pelo teclado. A key troca por aba para a lista entrar com fade.
        // grow + basis-0 (0px) + min-h-0: a lista ocupa só a sobra da altura do card e rola dentro dela. Não usar
        // flex-1: a base de 0% vira "tamanho do conteúdo" num card sem altura definida e esticaria a grade.
        // Transição entre abas: enquanto a nova carrega, a lista atual esmaece (opacity); quando chega, a key
        // troca e a nova entra com fade + leve subida (só transform/opacity; movimento reduzido desliga).
        // A entrada fica nesta camada externa porque o crm-scroll-fade do <ol> também usa a propriedade
        // `animation` e, no mesmo elemento, apagaria a animação de entrada. O fade de rolagem continua no <ol>,
        // que é o dono do scroll (regra do AGENTS.md).
        ? <div key={dimensaoExibida}
          className={cn(
            'flex min-h-0 grow basis-0 flex-col lg:min-h-[16.5rem] transition-opacity duration-200 animate-in fade-in-0 slide-in-from-bottom-2 [animation-duration:300ms] motion-reduce:animate-none motion-reduce:transition-none',
            trocando ? 'opacity-40' : 'opacity-100',
          )}>
          <ol tabIndex={0} aria-label={`Lista de ${configuracao.plural}`} aria-busy={trocando}
          className="crm-scroll crm-scroll-fade -mx-1 min-h-0 grow basis-0 divide-y divide-border overscroll-auto px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {itens.map((item, indice) => {
            const percentual = item.leads ? (item.conversoes / item.leads) * 100 : 0;
            const numeros = <span className="flex shrink-0 items-center gap-1 text-right text-sm tabular-nums text-muted-foreground"><strong className="font-semibold text-foreground">{item.leads}</strong><ArrowUpRight className="h-3.5 w-3.5 text-accent-foreground" aria-hidden="true" /><span className="sr-only">leads, {item.conversoes} conversões, {percentual.toFixed(1)}% de taxa</span></span>;

            // Conjunto/campanha: só leitura (não há apelido nem lista de leads por conjunto ainda).
            // Conjunto/campanha: a linha inteira é um botão que abre os leads daquele conjunto/campanha
            // (mesmo modal do clique no anúncio). Sem apelido: o nome vem da Meta.
            if (!porAnuncio) {
              const dimensaoLinha = dimensaoExibida as OrigemMetaClicada['dimensao'];
              // Durante a troca de aba a lista é provisória: o clique só vale para a lista definitiva.
              const clicavel = Boolean(onOrigemMetaClick) && !trocando;
              return <li key={item.chave}>
                <button type="button" disabled={!clicavel}
                  onClick={() => onOrigemMetaClick?.({ dimensao: dimensaoLinha, chave: item.chave, nome: item.nome, leads: item.leads })}
                  title={item.nome}
                  aria-label={`Ver leads ${dimensaoLinha === 'campanha' ? 'da campanha' : 'do conjunto'} ${item.nome}: ${item.leads} leads`}
                  className="grid min-h-11 w-full grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2 rounded-lg py-2 text-left transition-colors duration-150 enabled:hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default motion-reduce:transition-none">
                  <span className="text-xs tabular-nums text-muted-foreground">{indice + 1}</span>
                  <span className="line-clamp-2 break-words text-sm font-medium leading-snug text-foreground">{item.nome}</span>
                  {numeros}
                </button>
              </li>;
            }

            // Mantém o nome original no título e dá preferência ao apelido que veio do servidor.
            const nome = item.apelido ?? getAliasForAd(item.nome) ?? item.nome;
            const emEdicao = editando === item.nome;
            return <li key={item.chave} className="group grid min-h-11 grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2 py-1.5">
              <span className="text-xs tabular-nums text-muted-foreground">{indice + 1}</span>
              {emEdicao ? <div className="col-span-2 flex items-center gap-2 animate-in fade-in-0 duration-150 motion-reduce:animate-none">
                <Input
                  autoFocus
                  // Seleciona o nome atual ao abrir: digitar já substitui, e as setas permitem só ajustar.
                  onFocus={evento => evento.currentTarget.select()}
                  value={apelido}
                  onChange={evento => setApelido(evento.target.value)}
                  onKeyDown={evento => {
                    // Enter salva e Esc desiste, como no modal de busca.
                    if (evento.key === 'Enter') void salvar(item.nome);
                    if (evento.key === 'Escape') cancelar();
                  }}
                  aria-label={`Apelido de ${item.nome}`}
                  aria-invalid={Boolean(erroApelido)}
                  className="h-10 rounded-[14px] bg-card"
                />
                <Button type="button" size="icon" className="h-10 w-10 shrink-0 rounded-full" disabled={isSaving} onClick={() => void salvar(item.nome)} aria-label="Salvar apelido">
                  <Check className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button type="button" size="icon" variant="outline" className="h-10 w-10 shrink-0 rounded-full" onClick={cancelar} aria-label="Cancelar edição do apelido">
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div> : <>
                <div className="flex min-w-0 items-center gap-1">
                  <button type="button" disabled={!onAdClick} onClick={() => onAdClick?.(item.nome)} title={`Nome original: ${item.nome}`} className="min-h-10 min-w-0 truncate text-left text-sm font-medium text-foreground disabled:cursor-default disabled:opacity-100 hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{nome}</button>
                  {/* Lápis sempre visível no toque; no desktop aparece ao passar o mouse ou focar a linha. */}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={bloqueado}
                    onClick={() => iniciarEdicao(item.nome, nome)}
                    aria-label={`Editar apelido de ${nome}`}
                    title="Editar apelido"
                    className="h-8 w-8 shrink-0 rounded-full text-muted-foreground transition-opacity duration-150 hover:bg-accent hover:text-accent-foreground motion-reduce:transition-none md:opacity-0 md:focus-visible:opacity-100 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
                {numeros}
              </>}
            </li>;
          })}
        </ol>
        </div>
        : <div className="flex min-h-0 flex-1 items-center justify-center text-center"><div><div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-muted"><Target className="h-5 w-5 text-muted-foreground" aria-hidden="true" /></div><p className="text-sm text-muted-foreground">{porAnuncio ? 'Nenhum lead com anúncio identificado no período' : `Nenhum lead com ${configuracao.rotulo.toLocaleLowerCase('pt-BR')} identificado no período`}</p></div></div>}

    {/* Avisos de lista incompleta: a prévia tem só 10 itens e o servidor manda no máximo 500. */}
    {porAnuncio && erroLista && temItens && <p role="status" className="mt-2 shrink-0 text-xs text-muted-foreground">Mostrando só os primeiros. Não foi possível carregar a lista completa.</p>}
    {ranking.data && totalItens > itens.length && <p className="mt-2 shrink-0 text-xs text-muted-foreground">Mostrando os {itens.length} primeiros de {totalItens}.{porAnuncio ? ' Use Buscar para achar os demais.' : ''}</p>}
    {erroApelido && <p role="alert" className="mt-2 shrink-0 text-sm text-danger-text">{erroApelido}</p>}
    {apelidoSalvo && !editando && porAnuncio && <p role="status" className="mt-2 shrink-0 text-sm text-accent-foreground">Apelido salvo.</p>}

    {temItens && !carregandoAgrupado && <div className="mt-3 shrink-0 border-t border-border pt-3 text-sm">
      <div className="grid grid-cols-2 gap-3"><span><strong className="tabular-nums text-foreground">{totalLeads}</strong><span className="ml-1 text-muted-foreground">leads</span></span><span><strong className="tabular-nums text-foreground">{totalConversoes}</strong><span className="ml-1 text-muted-foreground">conversões</span></span></div>
      {/* Leads de anúncio que a Meta ainda não identificou: ficam fora do ranking, mas não somem da conta. */}
      {!porAnuncio && semOrigem > 0 && <p className="mt-1.5 text-xs text-muted-foreground">+ {semOrigem} {semOrigem === 1 ? 'lead' : 'leads'} de anúncio {configuracao.semOrigem}</p>}
    </div>}
    {modalAberto && !isUpdating && <AdPerformanceModal isOpen onClose={() => setModalAberto(false)} data={data} onAdClick={onAdClick} adminMode={adminMode} targetClinicaId={targetClinicaId} contexto={contexto} />}
  </div>;
};
